/**
 * Utility function for combining class names
 * Similar to clsx/classnames but lighter weight
 *
 * @example
 * cn('base-class', condition && 'conditional-class', 'another-class')
 * // Returns: 'base-class conditional-class another-class' or 'base-class another-class'
 */
export function cn(...classes: (string | undefined | null | false | 0)[]): string {
  return classes.filter(Boolean).join(' ');
}

/**
 * Utility for conditional class names with variants
 *
 * @example
 * cva('base-class', {
 *   variants: {
 *     size: { sm: 'text-sm', md: 'text-base', lg: 'text-lg' },
 *     color: { primary: 'text-primary-600', secondary: 'text-secondary-600' }
 *   },
 *   defaultVariants: { size: 'md', color: 'primary' }
 * })({ size: 'lg' })
 * // Returns: 'base-class text-lg text-primary-600'
 */
export function cva<T extends Record<string, Record<string, string>>>(
  base: string,
  config: {
    variants: T;
    defaultVariants?: { [K in keyof T]?: keyof T[K] };
  }
) {
  return (props?: { [K in keyof T]?: keyof T[K] }) => {
    const classes = [base];

    for (const variantKey in config.variants) {
      const variant = config.variants[variantKey];
      const value =
        props?.[variantKey as keyof typeof props] ??
        config.defaultVariants?.[variantKey as keyof typeof config.defaultVariants];

      if (value && variant[value as keyof typeof variant]) {
        classes.push(variant[value as keyof typeof variant]);
      }
    }

    return classes.join(' ');
  };
}

/**
 * Focus ring utility classes
 */
export const focusRing = {
  default: 'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
  inset: 'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-inset',
  visible: 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
  none: 'focus:outline-none focus:ring-0',
};

/**
 * Common transition classes
 */
export const transitions = {
  default: 'transition-all duration-150 ease-out',
  fast: 'transition-all duration-100 ease-out',
  slow: 'transition-all duration-300 ease-out',
  colors: 'transition-colors duration-150 ease-out',
  transform: 'transition-transform duration-200 ease-out',
  opacity: 'transition-opacity duration-150 ease-out',
};

/**
 * Disabled state classes
 */
export const disabled = {
  default: 'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none',
  subtle: 'disabled:opacity-60 disabled:cursor-not-allowed',
};

export default cn;
